import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, FONTS } from '../../tokens/design';
import { useMspClients, useMspAlerts, useMspSummary, useMspScanQueue } from '../../hooks/useMspData';
import { useClient } from '../../context/ClientContext';
import { getImpersonatedMspId, useApiClient } from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';
import { relativeTime, exactTime } from '../../utils/time';
import Shell from '../../components/layout/Shell';
import { useBranding } from '../../context/BrandingContext';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import PageError from '../../components/ui/PageError';
import TierBadge from '../../components/badges/TierBadge';
import SeverityBadge from '../../components/badges/SeverityBadge';
import { groupAlerts, isDemoPolishEnabled } from '../../utils/mspPolish';

function downloadFileNameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1].replace(/"/g, ''));
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const plain = header.match(/filename=([^;]+)/i);
  return plain?.[1]?.trim() || fallback;
}

async function readPackageError(res) {
  const text = await res.text().catch(() => '');
  if (!text?.trim()) return `Endpoint package generation failed (${res.status})`;
  try {
    const parsed = JSON.parse(text);
    return parsed?.error?.message || parsed?.error || parsed?.message || text;
  } catch {
    return text;
  }
}

const DEFAULT_CONNECTOR_FORM = {
  tunnelName: 'SentinelCommand',
  endpointAddressPool: '10.100.16.0/20',
  routerPublicKey: '',
  routerEndpoint: '',
  allowedIps: '10.100.0.1/32',
  scannerTunnelIp: '10.100.0.1',
  dns: '',
  persistentKeepaliveSeconds: 25,
  connectorGroup: 'pilot',
};

function connectorFormFromConfig(config) {
  return {
    tunnelName: config?.tunnelName || DEFAULT_CONNECTOR_FORM.tunnelName,
    endpointAddressPool: config?.endpointAddressPool || DEFAULT_CONNECTOR_FORM.endpointAddressPool,
    routerPublicKey: config?.routerPublicKey || DEFAULT_CONNECTOR_FORM.routerPublicKey,
    routerEndpoint: config?.routerEndpoint || DEFAULT_CONNECTOR_FORM.routerEndpoint,
    allowedIps: config?.allowedIps || DEFAULT_CONNECTOR_FORM.allowedIps,
    scannerTunnelIp: config?.scannerTunnelIp || DEFAULT_CONNECTOR_FORM.scannerTunnelIp,
    dns: config?.dns || DEFAULT_CONNECTOR_FORM.dns,
    persistentKeepaliveSeconds: config?.persistentKeepaliveSeconds || DEFAULT_CONNECTOR_FORM.persistentKeepaliveSeconds,
    connectorGroup: DEFAULT_CONNECTOR_FORM.connectorGroup,
  };
}

export default function MspDashboard() {
  const navigate = useNavigate();
  const { setClientId, setClientName } = useClient();
  const api = useApiClient();
  const { getAccessToken } = useAuth();
  const { data: MSP_CLIENTS, loading, error } = useMspClients();
  const { data: ALERTS, error: alertsError } = useMspAlerts();
  const branding = useBranding();
  const { data: SUMMARY, error: summaryError } = useMspSummary();
  const { running: SQ_RUNNING, queued: SQ_QUEUED, schedules: SQ_SCHEDULES, loading: sqLoading, error: sqError, refetch: sqRefetch } = useMspScanQueue();
  const [bulkScanning, setBulkScanning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState('');
  const [packageDownloadingClientId, setPackageDownloadingClientId] = useState(null);
  const [packageError, setPackageError] = useState('');
  const [connectorClient, setConnectorClient] = useState(null);
  const [connectorForm, setConnectorForm] = useState(DEFAULT_CONNECTOR_FORM);
  const [connectorLoading, setConnectorLoading] = useState(false);
  const [connectorSaving, setConnectorSaving] = useState(false);
  const [connectorError, setConnectorError] = useState('');
  const [retryPackageAfterSave, setRetryPackageAfterSave] = useState(false);

  const handleTriggerAllScans = async () => {
    if (MSP_CLIENTS.length === 0) return;
    setBulkScanning(true);
    setBulkProgress('');
    for (let i = 0; i < MSP_CLIENTS.length; i++) {
      const c = MSP_CLIENTS[i];
      const cId = c.clientId ?? c.id;
      const cName = c.name ?? c.companyName ?? cId;
      setBulkProgress(`Scanning ${i + 1} of ${MSP_CLIENTS.length} clients (${cName})...`);
      try {
        await api.post(`/clients/${cId}/scans`, { profileName: 'full' });
      } catch (err) {
        console.error(`[BulkScan] Failed for ${cName}:`, err);
      }
      if (i < MSP_CLIENTS.length - 1) {
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    setBulkProgress(`All ${MSP_CLIENTS.length} scans queued successfully.`);
    setBulkScanning(false);
    setTimeout(() => setBulkProgress(''), 5000);
  };

  const openConnectorConfig = async (client, retryAfterSave = false) => {
    const clientId = client?.clientId ?? client?.id;
    if (!clientId) return;
    const clientName = client?.name ?? client?.companyName ?? clientId;
    setConnectorClient({ clientId, name: clientName });
    setRetryPackageAfterSave(retryAfterSave);
    setConnectorLoading(true);
    setConnectorError('');
    try {
      const config = await api.get(`/clients/${clientId}/connectors/wireguard/config`);
      setConnectorForm(connectorFormFromConfig(config));
    } catch (err) {
      setConnectorForm(DEFAULT_CONNECTOR_FORM);
      setConnectorError(err.message || 'Unable to load connector settings.');
    } finally {
      setConnectorLoading(false);
    }
  };

  const updateConnectorForm = (key, value) => {
    setConnectorForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'scannerTunnelIp') {
        next.allowedIps = value ? `${value}/32` : '';
      }
      return next;
    });
  };

  const handleSaveConnectorConfig = async () => {
    if (!connectorClient?.clientId) return;
    setConnectorSaving(true);
    setConnectorError('');
    try {
      await api.put(`/clients/${connectorClient.clientId}/connectors/wireguard/config`, {
        tunnelName: connectorForm.tunnelName || 'SentinelCommand',
        endpointAddressPool: connectorForm.endpointAddressPool,
        routerPublicKey: connectorForm.routerPublicKey,
        routerEndpoint: connectorForm.routerEndpoint,
        allowedIps: connectorForm.allowedIps,
        scannerTunnelIp: connectorForm.scannerTunnelIp,
        dns: connectorForm.dns || null,
        persistentKeepaliveSeconds: Number(connectorForm.persistentKeepaliveSeconds) || 25,
      });

      const shouldDownload = retryPackageAfterSave;
      const savedClient = connectorClient;
      const connectorGroup = connectorForm.connectorGroup || 'pilot';
      setConnectorClient(null);
      setRetryPackageAfterSave(false);
      if (shouldDownload) {
        await handleDownloadEndpointPackage(savedClient.clientId, savedClient.name, connectorGroup);
      }
    } catch (err) {
      setConnectorError(err.message || 'Unable to save connector settings.');
    } finally {
      setConnectorSaving(false);
    }
  };

  const handleDownloadEndpointPackage = async (clientId, clientName, connectorGroup = 'pilot') => {
    if (!clientId) return;
    setPackageDownloadingClientId(clientId);
    setPackageError('');
    try {
      const token = await getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      const impersonatedMspId = getImpersonatedMspId();
      if (impersonatedMspId) headers['X-Act-As-Msp'] = impersonatedMspId;

      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const res = await fetch(`${baseUrl}/clients/${clientId}/connectors/deployment-package`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ connectorGroup }),
      });

      if (!res.ok) {
        const message = await readPackageError(res);
        if (message.includes('WireGuard connector settings must be saved')) {
          await openConnectorConfig({ clientId, name: clientName }, true);
          throw new Error('WireGuard connector settings are missing. Save the connector settings, then the ZIP will generate.');
        }
        throw new Error(message);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFileNameFromDisposition(
        res.headers.get('Content-Disposition'),
        `sentinel-endpoint-connector-${clientId}-${connectorGroup}.zip`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setPackageError(`${clientName || 'Client'}: ${err.message || 'Endpoint package download failed.'}`);
    } finally {
      setPackageDownloadingClientId(null);
    }
  };

  // Prefer real summary data from /msp/summary, fall back to client-derived
  const useSummary = !isDemoPolishEnabled();
  const visibleClientIds = new Set(MSP_CLIENTS.map(c => String(c.clientId ?? c.id ?? '').toLowerCase()).filter(Boolean));
  const summaryRows = Array.isArray(SUMMARY?.clients)
    ? SUMMARY.clients.filter(sc => visibleClientIds.has(String(sc.clientId ?? sc.id ?? '').toLowerCase()))
    : [];
  const totalClients = useSummary ? (SUMMARY?.totalClients ?? MSP_CLIENTS.length) : MSP_CLIENTS.length;
  const avgScore = useSummary ? (SUMMARY?.avgScore ?? SUMMARY?.averageScore ?? (MSP_CLIENTS.length > 0
    ? Math.round(MSP_CLIENTS.reduce((a, c) => a + (c.score ?? 0), 0) / MSP_CLIENTS.length)
    : 0)) : (MSP_CLIENTS.length > 0 ? Math.round(MSP_CLIENTS.reduce((a, c) => a + (c.score ?? 0), 0) / MSP_CLIENTS.length) : 0);
  const totalOpenFindings = useSummary
    ? (SUMMARY?.totalOpenFindings ?? MSP_CLIENTS.reduce((a, c) => a + (c.crit ?? 0) + (c.high ?? 0) + (c.med ?? 0), 0))
    : summaryRows.reduce((a, c) => a + (c.openFindings ?? c.findings ?? (c.crit ?? 0) + (c.high ?? 0) + (c.med ?? 0)), 0);
  const clientsAtRisk = useSummary ? (SUMMARY?.clientsAtRisk ?? MSP_CLIENTS.filter(c => typeof c.score === 'number' && c.score < 70).length) : MSP_CLIENTS.filter(c => typeof c.score === 'number' && c.score < 70).length;
  const scoreDelta = useSummary ? (SUMMARY?.scoreDelta ?? 0) : 0;
  const severityCounts = useSummary ? (SUMMARY?.severityCounts ?? {}) : summaryRows.reduce((acc, c) => {
    acc.critical += c.crit ?? 0;
    acc.high += c.high ?? 0;
    acc.medium += c.med ?? 0;
    return acc;
  }, { critical: 0, high: 0, medium: 0 });
  const scansThisWeek = useSummary ? (SUMMARY?.scansThisWeek ?? 0) : 0;
  const mttrDays = useSummary ? (SUMMARY?.mttrDays ?? null) : null;
  const postureTrend = useSummary && Array.isArray(SUMMARY?.postureTrend) ? SUMMARY.postureTrend : [];
  const topRiskyClients = useSummary && Array.isArray(SUMMARY?.topRiskyClients) ? SUMMARY.topRiskyClients : [];
  const criticalAlertGroups = groupAlerts(ALERTS, { severity: 'critical', limit: 8 });

  if (loading) {
    return (
      <Shell title="MSP Partner Dashboard" sub={`${branding?.portalName || 'MSP'} — Partner Console`}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 14 }}>
          {[0, 1, 2, 3].map(i => (
            <Card key={i} style={{ padding: '14px 18px' }}>
              <Skeleton width="60%" height={10} style={{ marginBottom: 8 }} />
              <Skeleton width="40%" height={28} style={{ marginBottom: 6 }} />
              <Skeleton width="50%" height={10} />
            </Card>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 12 }}>
          {[0, 1, 2].map(i => (
            <Card key={i} style={{ padding: 16 }}>
              <Skeleton width="70%" height={14} style={{ marginBottom: 10 }} />
              <Skeleton height={2} style={{ marginBottom: 10 }} />
              <div style={{ display: 'flex', gap: 8 }}>
                {[0, 1, 2].map(j => <Skeleton key={j} height={36} style={{ flex: 1 }} />)}
              </div>
            </Card>
          ))}
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell title="MSP Partner Dashboard" sub={`${branding?.portalName || 'MSP'} — Partner Console`}>
        <PageError error={error} />
      </Shell>
    );
  }

  return (
    <Shell title="MSP Partner Dashboard" sub={`${branding?.portalName || 'MSP'} — Partner Console`}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 14 }}>
        {[
          { l: 'Total Clients', v: totalClients, c: C.t1, sub: 'All active', needsScan: false },
          { l: 'Avg Security Score', v: avgScore > 0 ? avgScore : '\u2014', c: avgScore >= 80 ? C.grn : avgScore >= 60 ? C.amb : avgScore > 0 ? C.red : C.t3, sub: avgScore > 0 ? `${scoreDelta >= 0 ? '+' : ''}${scoreDelta} vs last period` : 'Run scans to calculate', needsScan: false },
          { l: 'Open Findings', v: totalOpenFindings, c: C.red, sub: 'Needs attention', needsScan: totalOpenFindings === 0 && MSP_CLIENTS.length > 0 },
          { l: 'Clients at Risk', v: clientsAtRisk, c: C.org, sub: 'Score below 70', needsScan: clientsAtRisk === 0 && MSP_CLIENTS.length > 0 },
        ].map(s => (
          <Card key={s.l} style={{ padding: '14px 18px', borderTop: `2px solid ${s.c}` }}>
            <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>{s.l}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.c, fontFamily: FONTS.mono }}>{s.v}</div>
            <div style={{ fontSize: 10, color: C.t2, marginTop: 3 }}>{s.sub}</div>
            {s.needsScan && <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 4, fontStyle: 'italic' }}>Available after first scan</div>}
          </Card>
        ))}
      </div>

      {(alertsError || summaryError) && <PageError error={alertsError || summaryError} />}

      <Card style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ marginRight: 'auto' }}>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1 }}>Quick Actions</div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>Demo-ready operations across active clients</div>
          </div>
          <Button variant="sec" onClick={() => { if (confirm(`This will trigger scans for all ${MSP_CLIENTS.length} clients. Proceed?`)) handleTriggerAllScans(); }} disabled={bulkScanning || MSP_CLIENTS.length === 0}>
            {bulkScanning ? 'Scanning...' : 'Trigger All Scans'}
          </Button>
          <Button variant="sec" disabled title="Report generation available after first scan completes">Generate All Reports</Button>
          <Button variant="pri" onClick={() => navigate('/msp/onboard')}>+ Add New Client</Button>
        </div>
        {bulkProgress && (
          <div style={{ fontSize: 10, color: bulkProgress.includes('successfully') ? C.grn : C.sky, fontFamily: FONTS.mono, marginTop: 10, lineHeight: 1.4 }}>{bulkProgress}</div>
        )}
      </Card>

      {MSP_CLIENTS.length === 0 && (
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans, marginBottom: 8 }}>No clients onboarded yet</div>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 16 }}>Add your first client to see dashboard metrics.</div>
          <Button variant="pri" onClick={() => navigate('/msp/onboard')}>+ Add Client</Button>
        </Card>
      )}

      {MSP_CLIENTS.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 12 }}>
          {MSP_CLIENTS.map(c => {
            const id = c.clientId ?? c.id ?? '';
            const name = c.name ?? c.companyName ?? '';
            const tier = c.tier || 'Baseline';
            const industry = c.industry ?? '';
            // Prefer score from summary.clients if available
            const summaryClient = summaryRows.find(sc => (sc.clientId ?? sc.id)?.toLowerCase() === id?.toLowerCase()) ?? null;
            const score = summaryClient?.score ?? c.score ?? null;
            const delta = c.delta ?? 0;
            const crit = summaryClient?.crit ?? c.crit ?? 0;
            const high = summaryClient?.high ?? c.high ?? 0;
            const med = summaryClient?.med ?? c.med ?? 0;
            const totalOpen = summaryClient?.openFindings ?? ((crit) + (high) + (med));
            const last = summaryClient?.lastScanAt ?? c.lastScanAt ?? c.lastScan ?? c.last ?? c.createdAt ?? null;
            const users = c.userCount ?? c.employeeCount ?? c.users ?? 0;
            const status = c.status ?? 'Active';
            const sc = typeof score === 'number' ? (score >= 80 ? C.grn : score >= 60 ? C.amb : C.red) : C.t3;
            return (
              <Card key={id} style={{ padding: 16, cursor: 'pointer', transition: 'all 0.15s', borderLeft: `3px solid ${status === 'warning' ? C.red : C.b1}` }}
                onClick={() => { setClientId(id); setClientName(name); navigate('/client/dashboard'); }}
                onMouseEnter={e => e.currentTarget.style.borderLeftColor = sc}
                onMouseLeave={e => e.currentTarget.style.borderLeftColor = status === 'warning' ? C.red : C.b1}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 4 }}>{name}</div>
                    <div style={{ display: 'flex', gap: 5 }}><TierBadge value={tier} /></div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: sc, fontFamily: FONTS.mono, lineHeight: 1 }}>{typeof score === 'number' ? score : '—'}</div>
                    {typeof score === 'number' && delta !== 0 && <div style={{ fontSize: 9, color: delta > 0 ? C.grn : C.red, fontFamily: FONTS.mono }}>{delta > 0 ? '+' : ''}{delta} pts</div>}
                  </div>
                </div>
                <div style={{ height: 2, background: C.b1, borderRadius: 1, marginBottom: 10, overflow: 'hidden' }}><div style={{ height: '100%', width: `${score ?? 0}%`, background: sc, borderRadius: 1 }} /></div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ l: 'Crit', v: crit, c: C.red }, { l: 'High', v: high, c: C.org }, { l: 'Med', v: med, c: C.amb }].map(f => (
                    <div key={f.l} style={{ flex: 1, textAlign: 'center', padding: '5px 3px', background: C.s2, borderRadius: 5 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: f.c, fontFamily: FONTS.mono }}>{f.v}</div>
                      <div style={{ fontSize: 8, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONTS.mono }}>{f.l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>
                  {totalOpen > 0 && <span style={{ color: C.org, fontWeight: 600 }}>{totalOpen} open findings</span>}
                  {totalOpen > 0 && ' · '}
                  Last scan: {last ? relativeTime(last) : '—'}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Per-client table */}
      {MSP_CLIENTS.length > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <CardHeader title="Client Overview" action={<span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{MSP_CLIENTS.length} clients</span>} />
          {packageError && (
            <div style={{ margin: '10px 12px 0', padding: '9px 12px', borderRadius: 6, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, fontSize: 10, fontFamily: FONTS.mono }}>
              {packageError}
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
                  {['Name', 'Score', 'Open Findings', 'Last Scan', 'Tier', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', fontSize: 9, color: C.t3, fontFamily: FONTS.mono, padding: '8px 12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', background: C.s2 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MSP_CLIENTS.map(c => {
                  const cId = c.clientId ?? c.id ?? '';
                  const cName = c.name ?? c.companyName ?? '';
                  const summaryClient = summaryRows.find(sc => (sc.clientId ?? sc.id) === cId) ?? null;
                  const cScore = summaryClient?.score ?? c.score ?? null;
                  const cOpen = summaryClient?.openFindings ?? ((c.crit ?? 0) + (c.high ?? 0) + (c.med ?? 0));
                  const cLast = summaryClient?.lastScanAt ?? c.lastScanAt ?? c.lastScan ?? c.last ?? c.createdAt ?? null;
                  const cTier = c.tier || 'Baseline';
                  const sc = typeof cScore === 'number' ? (cScore >= 80 ? C.grn : cScore >= 60 ? C.amb : C.red) : C.t3;
                  return (
                    <tr key={cId} style={{ borderBottom: `1px solid ${C.b2}`, cursor: 'pointer' }}
                      onClick={() => { setClientId(cId); setClientName(cName); navigate('/client/dashboard'); }}
                      onMouseEnter={e => e.currentTarget.style.background = C.s2}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans }}>{cName}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: sc, fontFamily: FONTS.mono }}>{typeof cScore === 'number' ? cScore : '\u2014'}</span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: cOpen > 0 ? C.red : C.t3, fontFamily: FONTS.mono }}>{cOpen}</td>
                      <td style={{ padding: '10px 12px', fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{cLast ? relativeTime(cLast) : '\u2014'}</td>
                      <td style={{ padding: '10px 12px' }}><TierBadge value={cTier} /></td>
                      <td style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <Button variant="sec" style={{ fontSize: 9 }} onClick={() => { setClientId(cId); setClientName(cName); navigate('/client/dashboard'); }}>View</Button>
                          <Button
                            variant="sec"
                            style={{ fontSize: 9 }}
                            disabled={Boolean(packageDownloadingClientId)}
                            onClick={() => handleDownloadEndpointPackage(cId, cName)}
                          >
                            {packageDownloadingClientId === cId ? 'Generating...' : 'Endpoint ZIP'}
                          </Button>
                          <Button
                            variant="sec"
                            style={{ fontSize: 9 }}
                            onClick={() => openConnectorConfig(c)}
                          >
                            Connector
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Scan Queue */}
      <Card style={{ marginBottom: 12 }}>
        <CardHeader title="Scan Queue" action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button variant="ghost" onClick={sqRefetch} style={{ fontSize: 9 }}>Refresh</Button>
            <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>
              {sqLoading ? 'Loading...' : `${SQ_RUNNING.length} running · ${SQ_QUEUED.length} queued · ${SQ_SCHEDULES.length} scheduled`}
            </span>
          </div>
        } />
        {sqError && <PageError error={sqError} onRetry={sqRefetch} />}
        {sqLoading && (
          <div style={{ padding: 14 }}>
            {[0, 1, 2].map(i => <Skeleton key={i} width="100%" height={18} style={{ marginBottom: 8 }} />)}
          </div>
        )}
        {!sqLoading && !sqError && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            {/* Running + Queued */}
            <div style={{ borderRight: `1px solid ${C.b1}` }}>
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: C.t1, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Active Jobs</span>
              </div>
              {SQ_RUNNING.length === 0 && SQ_QUEUED.length === 0 && (
                <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 11, color: C.t3, fontFamily: FONTS.mono }}>No active or queued jobs</div>
              )}
              {SQ_RUNNING.map(j => (
                <div key={j.jobId ?? j.id} style={{ padding: '9px 14px', borderBottom: `1px solid ${C.b2}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag label="Running" color={C.sky} />
                  <span style={{ fontSize: 11, color: C.t1, fontWeight: 600, flex: 1 }}>{j.clientName ?? 'Unknown'}</span>
                  <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>{j.startedAt ? `Started ${relativeTime(j.startedAt)}` : ''}</span>
                </div>
              ))}
              {SQ_QUEUED.map((j, i) => (
                <div key={j.jobId ?? j.id ?? i} style={{ padding: '9px 14px', borderBottom: `1px solid ${C.b2}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag label={`#${i + 1}`} color={C.amb} />
                  <span style={{ fontSize: 11, color: C.t1, fontWeight: 600, flex: 1 }}>{j.clientName ?? 'Unknown'}</span>
                  <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>{j.queuedAt ? `Queued ${relativeTime(j.queuedAt)}` : ''}</span>
                </div>
              ))}
            </div>
            {/* Schedules */}
            <div>
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: C.t1, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Schedules</span>
              </div>
              {SQ_SCHEDULES.length === 0 && (
                <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 11, color: C.t3, fontFamily: FONTS.mono }}>No schedules configured</div>
              )}
              {SQ_SCHEDULES.map(s => {
                const enabled = s.isEnabled !== false;
                const hasError = s.lastErrorMessage && s.retryCount > 0;
                return (
                  <div key={s.scheduleId ?? s.clientId} style={{ padding: '9px 14px', borderBottom: `1px solid ${C.b2}`, opacity: enabled ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 11, color: C.t1, fontWeight: 600, flex: 1 }}>{s.clientName ?? 'Unknown'}</span>
                      <Tag label={s.cronExpression ?? s.interval ?? 'custom'} color={C.t2} />
                      {!enabled && <Tag label="Paused" color={C.amb} />}
                    </div>
                    <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>
                      Next: {s.nextRunAt ? relativeTime(s.nextRunAt) : '\u2014'}
                      {hasError && <span style={{ color: C.red, marginLeft: 8 }}>Retry {s.retryCount}: {s.lastErrorMessage}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 12 }}>
        <Card>
          <CardHeader title="Cross-Client Critical Alerts" action={<Button variant="ghost" onClick={() => navigate('/msp/alerts')}>View all →</Button>} />
          {criticalAlertGroups.length === 0 && (
            <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>No critical alerts</div>
          )}
          {criticalAlertGroups.map(a => (
            <div key={a.id} style={{ padding: '11px 14px', borderBottom: `1px solid ${C.b2}`, display: 'flex', gap: 8, background: !a.read ? `${C.redBg}44` : 'transparent' }}>
              <SeverityBadge value="critical" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: C.sky, fontFamily: FONTS.mono, marginBottom: 2 }}>{a.client}</div>
                <div style={{ fontSize: 12, color: C.t1, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{a.msg}</span>
                  {a.count > 1 && <Tag label={`x${a.count}`} color={C.red} />}
                </div>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }} title={a.time ? exactTime(a.time) : ''}>{a.time ? relativeTime(a.time) : '\u2014'}</div>
              </div>
              {!a.read && <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.red, flexShrink: 0, marginTop: 4 }} />}
            </div>
          ))}
          {ALERTS.filter(a => a.severity === 'critical').length > criticalAlertGroups.length && (
            <div style={{ padding: '9px 14px', fontSize: 10, color: C.sky, fontFamily: FONTS.mono, cursor: 'pointer' }} onClick={() => navigate('/msp/alerts')}>
              View all grouped alerts →
            </div>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12 }}>Score Distribution</div>
            {[{ r: '80\u2013100', l: 'Healthy', n: MSP_CLIENTS.filter(c => (c.score ?? 0) >= 80).length, c: C.grn }, { r: '60\u201379', l: 'Fair', n: MSP_CLIENTS.filter(c => (c.score ?? 0) >= 60 && (c.score ?? 0) < 80).length, c: C.amb }, { r: '<60', l: 'At Risk', n: MSP_CLIENTS.filter(c => (c.score ?? 0) < 60).length, c: C.red }].map(b => (
              <div key={b.r} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                <div style={{ width: 44, fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textAlign: 'right' }}>{b.r}</div>
                <div style={{ flex: 1, height: 5, background: C.b1, borderRadius: 3, overflow: 'hidden' }}><div style={{ height: '100%', width: `${MSP_CLIENTS.length > 0 ? (b.n / MSP_CLIENTS.length) * 100 : 0}%`, background: b.c, borderRadius: 3 }} /></div>
                <span style={{ fontSize: 11, fontWeight: 700, color: b.c, fontFamily: FONTS.mono }}>{b.n}</span>
              </div>
            ))}
          </Card>
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10 }}>Operations KPIs</div>
            {[
              ['Critical / High', `${severityCounts.critical ?? 0} / ${severityCounts.high ?? 0}`, C.red],
              ['MTTR', mttrDays != null ? `${mttrDays}d` : '—', C.sky],
              ['Scans this week', scansThisWeek, C.grn],
            ].map(([label, value, color]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color, fontFamily: FONTS.mono }}>{value}</span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'end', gap: 3, height: 38, marginTop: 10 }}>
              {(postureTrend.length ? postureTrend : [{ score: avgScore }, { score: avgScore }, { score: avgScore }]).map((p, i) => (
                <div key={i} title={p.label || ''} style={{ flex: 1, height: `${Math.max(8, Math.min(36, Number(p.score || 0) * 0.36))}px`, background: C.sky, borderRadius: 3, opacity: 0.45 + (i * 0.1) }} />
              ))}
            </div>
          </Card>
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10 }}>Top Risky Clients</div>
            {(topRiskyClients.length ? topRiskyClients : MSP_CLIENTS.filter(c => typeof c.score === 'number').sort((a, b) => a.score - b.score).slice(0, 5)).map(c => (
              <div key={c.clientId ?? c.id ?? c.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 10, color: C.t1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name ?? c.Name}</span>
                <span style={{ fontSize: 10, color: (c.score ?? c.Score ?? 0) < 60 ? C.red : C.amb, fontFamily: FONTS.mono }}>{c.score ?? c.Score ?? '—'}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {connectorClient && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="WireGuard connector settings"
          onClick={() => { if (!connectorSaving) setConnectorClient(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(2, 6, 23, 0.62)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
        >
          <Card
            onClick={e => e.stopPropagation()}
            style={{ width: 'min(760px, 100%)', maxHeight: '88vh', overflowY: 'auto', padding: 18 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 14, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans }}>WireGuard Connector Settings</div>
                <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 4 }}>{connectorClient.name}</div>
              </div>
              <Button variant="ghost" style={{ fontSize: 9 }} disabled={connectorSaving} onClick={() => setConnectorClient(null)}>Close</Button>
            </div>

            {connectorLoading ? (
              <div style={{ padding: 18 }}>
                <Skeleton width="70%" height={14} style={{ marginBottom: 10 }} />
                <Skeleton width="100%" height={38} style={{ marginBottom: 10 }} />
                <Skeleton width="100%" height={38} />
              </div>
            ) : (
              <>
                <div style={{ padding: '9px 12px', borderRadius: 6, background: C.ambBg, border: `1px solid ${C.amb}33`, color: C.amb, fontSize: 10, fontFamily: FONTS.mono, marginBottom: 14, lineHeight: 1.5 }}>
                  These values come from the client WireGuard router and scanner path. They are required before the portal can generate endpoint deployment packages.
                </div>

                {connectorError && (
                  <div style={{ padding: '9px 12px', borderRadius: 6, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, fontSize: 10, fontFamily: FONTS.mono, marginBottom: 14 }}>
                    {connectorError}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    ['tunnelName', 'Tunnel Name', 'SentinelCommand'],
                    ['endpointAddressPool', 'Endpoint Address Pool', '10.100.16.0/20'],
                    ['routerPublicKey', 'Router Public Key', 'base64-public-key='],
                    ['routerEndpoint', 'Router Endpoint', '69.153.18.98:51820'],
                    ['scannerTunnelIp', 'Scanner Tunnel IP', '10.100.0.1'],
                    ['allowedIps', 'Allowed IPs', '10.100.0.1/32'],
                    ['dns', 'DNS', 'optional'],
                    ['connectorGroup', 'Connector Group', 'pilot'],
                  ].map(([key, label, placeholder]) => (
                    <label key={key} style={{ display: 'block' }}>
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
                      <input
                        value={connectorForm[key] ?? ''}
                        onChange={e => updateConnectorForm(key, e.target.value)}
                        placeholder={placeholder}
                        disabled={connectorSaving}
                        style={{ width: '100%', boxSizing: 'border-box', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '9px 10px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, outline: 'none' }}
                      />
                    </label>
                  ))}
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Keepalive Seconds</div>
                    <input
                      type="number"
                      min="0"
                      value={connectorForm.persistentKeepaliveSeconds}
                      onChange={e => updateConnectorForm('persistentKeepaliveSeconds', e.target.value)}
                      disabled={connectorSaving}
                      style={{ width: '100%', boxSizing: 'border-box', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '9px 10px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, outline: 'none' }}
                    />
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <Button variant="sec" disabled={connectorSaving} onClick={() => setConnectorClient(null)}>Cancel</Button>
                  <Button variant="pri" disabled={connectorSaving} onClick={handleSaveConnectorConfig}>
                    {connectorSaving ? 'Saving...' : retryPackageAfterSave ? 'Save & Download ZIP' : 'Save Settings'}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </Shell>
  );
}
